'use client';

import { useContext, useEffect, useMemo, useState } from 'react';

import { api } from '@/lib/convex';
import { useQuery } from '@/lib/server-seed/seeded-convex-hooks';
import { createIdentityScopedSessionMemory } from '@/lib/identity-scoped-session-memory';
import { ConfirmedConvexIdentityContext } from '@/providers/protected-workspace-boundary';
import { resolveResearchReportMarkdownSource } from '@/components/chat/tool-calls/research-report-markdown-source';

export interface AuthorizedWorkspaceMarkdownState {
  error: boolean;
  key: string | null;
  loading: boolean;
  text: string | null;
}

type RemoteMarkdownState = {
  contentKey: string;
  error: boolean;
  key: string;
  text: string | null;
};

const rememberedMarkdownByRevision =
  createIdentityScopedSessionMemory<string>(20);

function markdownMemoryKey(orgSlug: string, key: string): string {
  return `${orgSlug}\n${key}`;
}

/**
 * Session memory of document text this principal loaded, by workspace and
 * file revision (`key`). A card remounted when its turn settles gets its
 * `readFile` result at once from the still-live cached subscription, but a
 * remote document's text is a plain fetch per mount (#4165). The memory is
 * used only after that live read has answered `remote` for this identity
 * (access confirmed now), while the fetch is pending; while the read is
 * still loading, or after it refuses, retained text is never shown. Bound to
 * the confirmed identity and wiped when it changes; a refusal forgets it.
 */
export function rememberWorkspaceMarkdown(
  identity: string | null,
  orgSlug: string,
  key: string,
  text: string
): void {
  rememberedMarkdownByRevision.set(
    identity,
    markdownMemoryKey(orgSlug, key),
    text
  );
}

export function rememberedWorkspaceMarkdown(
  identity: string | null,
  orgSlug: string,
  key: string
): string | null {
  return rememberedMarkdownByRevision.get(
    identity,
    markdownMemoryKey(orgSlug, key)
  );
}

/** Test hook. */
export function resetRememberedWorkspaceMarkdownForTests(): void {
  rememberedMarkdownByRevision.clear();
}

/**
 * Signed download URLs rotate while the file revision (`contentKey`) stays
 * the same. Keep showing the loaded text for that revision while the new URL
 * refetches, so the card does not drop back to its loading shape.
 */
export function resolveRemoteMarkdownState(
  contentKey: string,
  requestKey: string,
  remoteState: RemoteMarkdownState
): AuthorizedWorkspaceMarkdownState {
  if (remoteState.key === requestKey) {
    return {
      error: remoteState.error,
      key: contentKey,
      loading: false,
      text: remoteState.text,
    };
  }
  if (remoteState.contentKey === contentKey && remoteState.text !== null) {
    return {
      error: false,
      key: contentKey,
      loading: false,
      text: remoteState.text,
    };
  }
  return { error: false, key: contentKey, loading: true, text: null };
}

export function useAuthorizedWorkspaceMarkdown(args: {
  enabled: boolean;
  key: string | null;
  legacyUrl?: string | null;
  orgSlug: string;
  path?: string | null;
}): AuthorizedWorkspaceMarkdownState {
  const queryEnabled =
    args.enabled && args.orgSlug.length > 0 && Boolean(args.path);
  const path = args.path;
  // Seeded: a server-rendered thread paints the document without waiting
  // for the socket; the live subscription takes over when it delivers.
  const readResult = useQuery(
    api.vfs.public.readFile,
    queryEnabled && path ? { orgSlug: args.orgSlug, path } : 'skip'
  );
  const source = useMemo(
    () =>
      resolveResearchReportMarkdownSource({
        readResult,
        legacyUrl: args.legacyUrl,
        queryEnabled,
      }),
    [args.legacyUrl, queryEnabled, readResult]
  );
  const [remoteState, setRemoteState] = useState<RemoteMarkdownState>({
    contentKey: '',
    error: false,
    key: '',
    text: null,
  });

  useEffect(() => {
    if (!args.enabled || !args.key || source.kind !== 'remote') return;
    const contentKey = args.key;
    const requestKey = `${contentKey}:${source.url}`;
    const controller = new AbortController();
    void fetch(source.url, { signal: controller.signal })
      .then((response) => {
        if (!response.ok) throw new Error('WORKSPACE_MARKDOWN_LOAD_FAILED');
        return response.text();
      })
      .then((text) =>
        setRemoteState({ contentKey, error: false, key: requestKey, text })
      )
      .catch(() => {
        if (!controller.signal.aborted) {
          setRemoteState({
            contentKey,
            error: true,
            key: requestKey,
            text: null,
          });
        }
      });
    return () => controller.abort();
  }, [args.enabled, args.key, source]);

  const identity = useContext(ConfirmedConvexIdentityContext);
  const state = resolveAuthorizedWorkspaceMarkdownState(
    args,
    source,
    remoteState,
    rememberedWorkspaceMarkdown(identity, args.orgSlug, args.key ?? '')
  );
  const orgSlug = args.orgSlug;
  const stateKey = state.key;
  const stateText = state.text;
  const refused = state.error;
  useEffect(() => {
    if (!stateKey) return;
    if (refused) {
      rememberedMarkdownByRevision.forget(markdownMemoryKey(orgSlug, stateKey));
    } else if (stateText !== null) {
      rememberWorkspaceMarkdown(identity, orgSlug, stateKey, stateText);
    }
  }, [identity, orgSlug, refused, stateKey, stateText]);
  return state;
}

function resolveAuthorizedWorkspaceMarkdownState(
  args: { enabled: boolean; key: string | null },
  source: ReturnType<typeof resolveResearchReportMarkdownSource>,
  remoteState: RemoteMarkdownState,
  remembered: string | null
): AuthorizedWorkspaceMarkdownState {
  if (!args.enabled || !args.key) {
    return { error: false, key: null, loading: false, text: null };
  }
  if (source.kind === 'inline') {
    return { error: false, key: args.key, loading: false, text: source.text };
  }
  if (source.kind === 'error') {
    return { error: true, key: args.key, loading: false, text: null };
  }
  if (source.kind === 'loading') {
    // Access not yet confirmed by the live read: never show retained text.
    return { error: false, key: args.key, loading: true, text: null };
  }
  const state = resolveRemoteMarkdownState(
    args.key,
    `${args.key}:${source.url}`,
    remoteState
  );
  if (!state.loading) return state;
  return remembered === null
    ? state
    : { error: false, key: args.key, loading: false, text: remembered };
}
